import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import prettyMilliseconds from 'pretty-ms'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { MediaTypes, downloadMedia, fetchMediaDetails } from '../../../../actions/content'
import { ACL, colors, Media } from '../../../../constants'
import * as MediaSelectors from '../../../../reducers/content/media/selectors'
import { Button, Centered, Column, Confirmation, Headings, Loader, Margin, Padding, Thumbnail } from '../../../../components'
import { EstateUtils, FileUtils } from '../../../../utils'
import MediaApprovals from './MediaApprovals'
import { Background, BackLink, Box, MediaPlaylistsDetails } from './MediaDetails.style'
import PushStatus from './PushStatus'

function MediaDetails() {
    const history = useHistory()
    const { media } = useParams()
    const mediaId = parseInt(media, 10)
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const isWorking = useSelector(MediaSelectors.isWorking)
    const selectedMedia = useSelector(MediaSelectors.selectedMedia)
    const isDownloadBlocked = useSelector((state) => EstateUtils.checkACL(state, ACL.DOWNLOAD_BLOCKED))

    useEffect(() => {
        dispatch(fetchMediaDetails(mediaId))
    }, [dispatch, mediaId])

    const {
        created_at,
        file_size,
        height,
        is_approved: isApproved,
        is_tested: isTested,
        length,
        name,
        width,
        tags,
        uploaded_by,
        chain_id,
        playlists,
    } = selectedMedia || {}

    const [, setMediaTags] = useState(tags !== undefined && tags.length > 0 ? tags.map((tag) => tag.id) : [])

    useEffect(() => {
        setMediaTags(tags !== undefined && tags.length > 0 ? tags.map((tag) => tag.id) : [])
    }, [tags])

    // Approval and testing status
    const hasBeenApproved = isApproved === Media.approvalStatus.approved
    const hasBeenTestedOk = isTested === Media.testingStatus.approved
    const isApprovedAndTested = hasBeenApproved && hasBeenTestedOk

    const formatDate = (date) => {
        if (!date) return 'N/A'
        return moment(new Date(date)).fromNow()
    }

    const onGoBackClick = () => {
        history.goBack()
    }

    const onDownloadMediaClick = async () => {
        if (isDownloadBlocked) {
            return null
        }

        const response = await dispatch(downloadMedia(mediaId, false))

        if (response.type !== MediaTypes.DOWNLOAD_MEDIA_SUCCESS) {
            return null
        }

        const url = response.payload
        window.open(url, '_blank')
    }

    const renderPlaylistLabel = () => {
        if (playlists) {
            if (playlists.length === 0) {
                return null
            }

            if (playlists.length === 1) {
                return t('media-in-single-playlist', { count: playlists.length })
            }

            return t('media-in-playlist', { count: playlists.length })
        }
    }

    const renderBody = () => (
        <Padding size="lg">
            <Headings.H1 bold color={colors.blue}>
                {name}
            </Headings.H1>
            <Padding top size="xs">
                <Headings.H2 color={colors.grey}>
                    &#35;
                    {mediaId}
                </Headings.H2>
            </Padding>
            <Padding top size="xs">
                <Thumbnail mediaId={mediaId} withPlayer />
            </Padding>
            <Padding top size="xs">
                <Button onClick={onDownloadMediaClick} theme="secondary" size="md" disabled={isDownloadBlocked}>
                    <FontAwesomeIcon icon="download" />
                    &nbsp;
                    {t('download')}
                </Button>
                {false && (
                    <Confirmation
                        confirmation={t('content-push-confirmation')}
                        header={t('content-push-confirmation', {
                            context: 'title',
                        })}
                        ok={t('ok')}
                        cancel={t('cancel')}
                        onCancel={() => null}
                        onConfirm={() => null}
                    >
                        <Button size="md" theme="secondary" title={t('content-push-description')}>
                            <FontAwesomeIcon icon="cloud-download-alt" />
                            &nbsp;&nbsp;
                            {!isApprovedAndTested && t('content-push-description-test')}
                            {isApprovedAndTested && t('content-push-description')}
                        </Button>
                    </Confirmation>
                )}
            </Padding>
            <Padding top size="md">
                <Padding left right size="md">
                    <Column>
                        <Column right padding="0 5px 0 0" width="25%">
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.grey}>{t('file-name')}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.grey}>{t('file-added')}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.grey}>{t('file-length')}</Headings.H3>
                            </Margin>
                            <Headings.H3 color={colors.grey}>{t('Chain-id')}</Headings.H3>
                        </Column>
                        <Column width="75%">
                            <Margin bottom size="xs" textTruncated>
                                <Headings.H3 color={colors.bluedark}>{name}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.bluedark}>{formatDate(created_at)}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.bluedark}>{prettyMilliseconds(length || 0)}</Headings.H3>
                            </Margin>
                            <Headings.H3 color={colors.bluedark}>{chain_id}</Headings.H3>
                        </Column>
                    </Column>
                    <Column>
                        <Column right padding="0 5px 0 0" width="25%">
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.grey}>{t('resolution')}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.grey}>{t('file-size')}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.grey}>{'Uploaded by'}</Headings.H3>
                            </Margin>
                        </Column>
                        <Column width="75%">
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.bluedark}>{`${width} x ${height}`}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs">
                                <Headings.H3 color={colors.bluedark}>{FileUtils.humanFileSize(file_size)}</Headings.H3>
                            </Margin>
                            <Margin bottom size="xs" textTruncated>
                                <Headings.H3 color={colors.bluedark}>{uploaded_by}</Headings.H3>
                            </Margin>
                        </Column>
                        <MediaPlaylistsDetails>{renderPlaylistLabel()}</MediaPlaylistsDetails>
                    </Column>
                </Padding>
            </Padding>
            <MediaApprovals isWorking={isWorking} />
            {isApprovedAndTested && <PushStatus />}
            {/* <TagForm addTag={addTag} />
            <Tags mediaTags={mediaTags} setMediaTags={setMediaTags} removeTag={removeTag}/> */}
        </Padding>
    )

    return (
        <Background horizontal>
            <BackLink onClick={onGoBackClick}>
                <FontAwesomeIcon icon="chevron-left" />
                {t('back')}
            </BackLink>
            <Box>
                {isWorking && (
                    <Centered horizontal vertical>
                        <Loader />
                    </Centered>
                )}
                {!isWorking && renderBody()}
            </Box>
        </Background>
    )
}

export default MediaDetails
